'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, createContext, useMemo, useCallback, useReducer, useState } from 'react';
import { Dialog, DialogContentWrapper } from '@/comps/primitives/dialog';
type DialogReference = {
  id: string;
  content: ReactNode;
  preventClose?: boolean;
  classNames?: {
    contentWrapper?: string;
  };
};
type DialogContextType = {
  push: (dialog: Omit<DialogReference, 'id'>, id?: string) => {
    id: string;
    pop: () => void;
  };
  removeById: (id: string) => void;
  clear: () => void;
};
type DialogActionContextType = {
  close: () => void;
};
export const DialogContext = createContext<DialogContextType | null>(null);
export const DialogActionContext = createContext<DialogActionContextType | null>(null);
type DialogAction = {
  type: 'push';
  dialog: DialogReference;
} | {
  type: 'removeById';
  id: string;
} | {
  type: 'pop';
} | {
  type: 'clear';
};
function stateReducer(state: DialogReference[], action: DialogAction): DialogReference[] {
  switch (action.type) {
    case 'push':
      return [...state, action.dialog];
    case 'removeById':
      {
        const itemIdx = state.findIndex(m => m.id === action.id);
        return state.toSpliced(itemIdx, 1);
      }
    case 'clear':
      return [];
    case 'pop':
      {
        return state.slice(0, -1);
      }
    default:
      return state;
  }
}
export const DialogProvider = t0 => {
  const $ = _c(29);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dialogs, dispatch] = useReducer(stateReducer, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [onCloseMap] = useState(t2);
  let t3;
  if ($[2] !== onCloseMap) {
    t3 = id => {
      const onClose = onCloseMap.get(id);
      onClose?.();
      onCloseMap.delete(id);
      dispatch({
        type: "removeById",
        id
      });
    };
    $[2] = onCloseMap;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const removeById = t3;
  let t4;
  if ($[4] !== dialogs || $[5] !== onCloseMap) {
    t4 = () => {
      const last = dialogs.at(-1);
      if (last) {
        const onClose_0 = onCloseMap.get(last.id);
        onClose_0?.();
        onCloseMap.delete(last.id);
      }
      dispatch({
        type: "pop"
      });
    };
    $[4] = dialogs;
    $[5] = onCloseMap;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const pop = t4;
  let t5;
  if ($[7] !== onCloseMap || $[8] !== removeById) {
    t5 = (dialog, $id) => {
      const id_0 = $id ?? `dialog-${random()}`;
      dispatch({
        type: "push",
        dialog: {
          id: id_0,
          ...dialog
        }
      });
      if (dialog.onClose) {
        onCloseMap.set(id_0, dialog.onClose);
      }
      return {
        id: id_0,
        pop: () => {
          removeById(id_0);
        }
      };
    };
    $[7] = onCloseMap;
    $[8] = removeById;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const push = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      dispatch({
        type: "clear"
      });
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const clear = t6;
  let t7;
  if ($[11] !== push || $[12] !== removeById) {
    t7 = {
      push,
      removeById,
      clear
    };
    $[11] = push;
    $[12] = removeById;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const contextValue = t7;
  let t8;
  if ($[14] !== dialogs) {
    t8 = dialogs.at(-1);
    $[14] = dialogs;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const last_0 = t8;
  const t9 = last_0 != undefined;
  let t10;
  if ($[16] !== pop) {
    t10 = open => !open && pop();
    $[16] = pop;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== last_0 || $[19] !== removeById) {
    t11 = last_0 && <DialogActionContext.Provider value={{
      close: () => removeById(last_0.id)
    }}><DialogContentWrapper onInteractOutside={e => {
        if (last_0.preventClose) {
          e.preventDefault();
        }
      }} className={last_0.classNames?.contentWrapper}>{last_0.content}</DialogContentWrapper></DialogActionContext.Provider>;
    $[18] = last_0;
    $[19] = removeById;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t9) {
    t12 = <Dialog open={t9} onOpenChange={t10}>{t11}</Dialog>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== children || $[26] !== contextValue || $[27] !== t12) {
    t13 = <DialogContext.Provider value={contextValue}>{children}{t12}</DialogContext.Provider>;
    $[25] = children;
    $[26] = contextValue;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
};
function random() {
  return Math.floor(Math.random() * 1000).toString().padStart(3, '0');
}